import type {
  ManagedAgentId,
  ManagedEntrypointKind,
  ManagedPromptKind,
} from "./types.js";

const COMMAND_MARKER = "emergent-thinking:managed-command";
const PROMPT_FILE_MARKER = "emergent-thinking:managed-prompt-file";
const COMMAND_DESCRIPTION = "Force emergent-thinking workflow for the given task";
const PROMPT_DESCRIPTION = "Default to emergent-thinking for coding and repository work";

export function getManagedCommandMarker() {
  return COMMAND_MARKER;
}

export function getManagedPromptFileMarker() {
  return PROMPT_FILE_MARKER;
}

export function renderManagedCommand(
  agentId: ManagedAgentId,
  kind: ManagedEntrypointKind,
) {
  const sharedBody = renderEntrypointBody(agentId);

  switch (kind) {
    case "claude-command":
      return `---
description: ${COMMAND_DESCRIPTION}
argument-hint: [task]
---
<!-- ${COMMAND_MARKER} -->

${sharedBody}
`;
    case "cline-workflow":
    case "cursor-command":
      return `<!-- ${COMMAND_MARKER} -->

${sharedBody}
`;
    case "copilot-prompt":
      return `---
mode: agent
description: ${COMMAND_DESCRIPTION}
---
<!-- ${COMMAND_MARKER} -->

${sharedBody}
`;
    case "gemini-command":
      return [
        `description = "${COMMAND_DESCRIPTION}"`,
        "",
        `# ${COMMAND_MARKER}`,
        "",
        'prompt = """',
        `${sharedBody}`,
        '"""',
        "",
      ].join("\n");
    case "opencode-command":
      return `---
description: ${COMMAND_DESCRIPTION}
---
${COMMAND_MARKER}

${sharedBody}
`;
    case "qoder-command":
      return `---
name: emergent-thinking
description: ${COMMAND_DESCRIPTION}
---
<!-- ${COMMAND_MARKER} -->

${sharedBody}
`;
    case "markdown-command":
      return `<!-- ${COMMAND_MARKER} -->

${sharedBody}
`;
  }
}

export function renderGlobalPromptBlock() {
  return `<emergent-thinking>
For coding and repository work, default to the installed emergent-thinking skill plus the local emergent-thinking CLI instead of freeform implementation.

Required workflow:
1. Frame or reuse a board before major edits.
2. Write down the current problem, the goal, and one bounded path.
3. Validate early, record failures as findings, and record successful checks as proof.
4. Repair the cheapest meaningful stage, then loop until the board converges.

If the task is trivial or purely conversational, you may answer directly.
</emergent-thinking>
`;
}

export function renderManagedPromptFile(kind: ManagedPromptKind) {
  const block = renderGlobalPromptBlock().trimEnd();

  switch (kind) {
    case "append-block":
    case "cline-rule":
      return `<!-- ${PROMPT_FILE_MARKER} -->

${block}
`;
    case "cursor-rule":
    case "firebender-rule":
      return `---
description: ${PROMPT_DESCRIPTION}
alwaysApply: true
---
<!-- ${PROMPT_FILE_MARKER} -->

${block}
`;
  }
}

function renderEntrypointBody(agentId: ManagedAgentId) {
  const taskVariable =
    agentId === "gemini-cli"
      ? "{{args}}"
      : agentId === "opencode" || agentId === "claude-code" || agentId === "codex"
        ? "$ARGUMENTS"
        : "";

  const taskSection =
    taskVariable.length === 0
      ? [
          "Task:",
          "Use any task text supplied alongside this entrypoint as the target task.",
          "If no task text was supplied, ask for it before proceeding.",
        ].join("\n")
      : ["Task:", taskVariable, "", "If the task argument is empty, ask for it before proceeding."].join("\n");

  return [
    "Use the installed `emergent-thinking` skill together with the local `emergent-thinking` CLI.",
    "",
    taskSection,
    "",
    "Requirements:",
    "1. Do not solve the task freehand. Use emergent-thinking as the working protocol.",
    "2. Frame or reuse a board in the current project before substantial edits.",
    "3. Write the key problem, goal, path, tool choice, findings, and proof debt onto the board.",
    "4. Let validation results drive the next move. Record failed checks as findings and passed checks as proof.",
    "5. Iterate until the task converges or you hit a real blocker.",
    "",
    "Start by restating the task, then enter the emergent-thinking workflow and keep the board updated while you work.",
  ].join("\n");
}
